import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
  LineChart, Line,
} from 'recharts';
import { RefreshCw, FileSearch, TrendingUp, ShieldCheck, AlertTriangle } from 'lucide-react';
import { motion } from 'framer-motion';
import { FilterPanel } from '@/components/FilterPanel';
import { TrustScoreBadge } from '@/components/TrustScoreBadge';
import { useCurrency } from '@/lib/useCurrency';
import { useGlobalFilters } from '@/lib/FilterContext';
import { HelpBubble } from '@/components/HelpBubble';
import api from '@/lib/api';

function KpiTile({ label, value, sub, icon: Icon, color, testId, help }) {
  return (
    <Card className="border-l-4 hover:shadow-lg transition-shadow" style={{ borderLeftColor: color }} data-testid={testId}>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-xs font-medium text-muted-foreground whitespace-nowrap truncate flex items-center gap-1">
          {label}
          {help ? <HelpBubble text={help} /> : null}
        </CardTitle>
        <Icon className="w-5 h-5 shrink-0" style={{ color }} />
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold font-mono tracking-tight truncate">{value}</div>
        <p className="text-xs text-muted-foreground mt-1 truncate">{sub}</p>
      </CardContent>
    </Card>
  );
}

export default function OnContractMaverick() {
  const { symbol: cs } = useCurrency();
  const { filters } = useGlobalFilters();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [drillOpen, setDrillOpen] = useState(null);
  const [drillRows, setDrillRows] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters || {}).forEach(([k, v]) => { if (v) params.append(k === 'startDate' ? 'start_date' : k === 'endDate' ? 'end_date' : k, v); });
      const res = await api.get(`/analytics/on-contract-maverick${params.toString() ? `?${params.toString()}` : ''}`);
      setData(res.data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const openDrill = async (supplier) => {
    setDrillOpen(supplier);
    setDrillRows([]);
    try {
      const res = await api.get(`/analytics/on-contract-maverick/drill-down?supplier=${encodeURIComponent(supplier)}&limit=100`);
      setDrillRows(res.data.rows || []);
    } catch { setDrillRows([]); }
  };

  const k = data?.kpis || {};
  const fmt = (v) => `${cs}${(v || 0).toLocaleString()}`;

  return (
    <div className="p-6 space-y-4" data-testid="on-contract-maverick-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">On-Contract vs Maverick</h1>
          <p className="text-sm text-muted-foreground mt-1">
            PO-based split. Rows with a populated <code className="text-xs bg-muted px-1 py-0.5 rounded">po_number</code> are on-contract;
            everything else is maverick. See <span className="italic">P2P Analytics</span> for the stricter composite definition.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <TrustScoreBadge pageName="on-contract-maverick" />
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="refresh-button">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />Refresh
          </Button>
        </div>
      </div>

      <FilterPanel
        filters={filters}
        onFiltersChange={() => {}}
        pageName="on-contract-maverick"
        visibleFilters={['category', 'category_l2', 'supplier', 'region', 'country', 'sbu', 'fiscal_year', 'cost_center', 'business_unit']}
        showDateRange={true}
      />

      {!data?.has_data ? (
        <Card><CardContent className="py-12 text-center text-muted-foreground">No data available</CardContent></Card>
      ) : (
        <>
          {/* KPI Row */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <KpiTile
              label="ON-CONTRACT SPEND" value={fmt(k.on_contract_spend)}
              sub={`${k.on_contract_pct}% of total · ${k.on_contract_tx?.toLocaleString()} txns`}
              icon={ShieldCheck} color="#10b981" testId="on-contract-kpi"
              help="Spend that is BOTH governed by an active contract AND raised through a PO. This is the strict definition — the gold standard for procurement governance. Target 80%+."
            />
            <KpiTile
              label="MAVERICK SPEND" value={fmt(k.maverick_spend)}
              sub={`${k.maverick_pct}% of total · ${k.maverick_tx?.toLocaleString()} txns`}
              icon={AlertTriangle} color="#ef4444" testId="maverick-kpi"
              help="Strict Maverick = spend WITHOUT a linked PO. Different from the composite Maverick on P2P Analytics (which also flags off-contract). Every £ here is a process leak: no PO = no approval trail."
            />
            <KpiTile
              label="TOTAL SPEND" value={fmt(k.total_spend)}
              sub={`${k.total_transactions?.toLocaleString()} transactions`}
              icon={TrendingUp} color="#3b82f6" testId="total-kpi"
              help="Total spend in the current filtered view, FX-normalised. Matches the Dashboard's TOTAL SPEND. The sum of On-Contract + Maverick should equal this number."
            />
            <KpiTile
              label="COVERAGE" value={`${k.on_contract_pct}%`}
              sub="% of spend covered by a PO"
              icon={FileSearch} color="#8b5cf6" testId="coverage-kpi"
              help="Percentage of spend backed by a PO. Same figure as On-Contract Spend % here because the strict definition uses PO-coverage as its test. Mature procurement teams aim for 85%+."
            />
          </div>

          {/* Trend */}
          {data.monthly_trend?.length >= 3 && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              <Card data-testid="maverick-trend-chart">
                <CardHeader>
                  <CardTitle>Monthly Trend</CardTitle>
                  <CardDescription>On-contract vs maverick spend over time</CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={260}>
                    <LineChart data={data.monthly_trend}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" tick={{ fontSize: 11 }} />
                      <YAxis stroke="hsl(var(--muted-foreground))" tickFormatter={(v) => `${cs}${(v / 1000).toFixed(0)}k`} />
                      <Tooltip formatter={(val) => `${cs}${val?.toLocaleString()}`} />
                      <Legend />
                      <Line type="monotone" dataKey="on_contract" stroke="#10b981" strokeWidth={2} name="On-Contract" />
                      <Line type="monotone" dataKey="maverick" stroke="#ef4444" strokeWidth={2} name="Maverick" />
                    </LineChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* Top 5 maverick suppliers */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
            <Card data-testid="top-maverick-suppliers">
              <CardHeader>
                <CardTitle>Top 5 Maverick Suppliers</CardTitle>
                <CardDescription>Suppliers with the most off-PO spend. Click a row to drill into their transactions.</CardDescription>
              </CardHeader>
              <CardContent>
                {data.top_maverick_suppliers.length === 0 ? (
                  <p className="text-center text-muted-foreground py-6">No maverick suppliers found</p>
                ) : (
                  <ResponsiveContainer width="100%" height={240}>
                    <BarChart data={data.top_maverick_suppliers} layout="vertical" margin={{ left: 30 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis type="number" tickFormatter={(v) => `${cs}${(v / 1000).toFixed(0)}k`} stroke="hsl(var(--muted-foreground))" />
                      <YAxis dataKey="supplier" type="category" width={160} stroke="hsl(var(--muted-foreground))" tick={{ fontSize: 11 }} />
                      <Tooltip formatter={(val) => `${cs}${val?.toLocaleString()}`} />
                      <Bar dataKey="spend" fill="#ef4444" radius={[0, 4, 4, 0]} onClick={(e) => openDrill(e.supplier)} cursor="pointer" />
                    </BarChart>
                  </ResponsiveContainer>
                )}
                <div className="mt-4 space-y-1">
                  {data.top_maverick_suppliers.map((s, i) => (
                    <button
                      key={s.supplier}
                      className="w-full text-left flex justify-between items-center py-2 px-3 rounded hover:bg-muted transition-colors"
                      onClick={() => openDrill(s.supplier)}
                      data-testid={`maverick-supplier-row-${i}`}
                    >
                      <span className="font-medium text-sm">{s.supplier}</span>
                      <span className="font-mono text-sm">{fmt(s.spend)} · {s.tx_count} txns</span>
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Drill-down panel */}
          {drillOpen && (
            <Card data-testid="maverick-drill-down">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Maverick transactions: {drillOpen}</CardTitle>
                  <CardDescription>Up to 100 most recent off-PO transactions</CardDescription>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setDrillOpen(null)}>Close</Button>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2 px-2">Date</th>
                        <th className="text-left py-2 px-2">Invoice #</th>
                        <th className="text-left py-2 px-2">Category</th>
                        <th className="text-left py-2 px-2">Buyer</th>
                        <th className="text-left py-2 px-2">GL / Cost Ctr</th>
                        <th className="text-right py-2 px-2">Spend</th>
                      </tr>
                    </thead>
                    <tbody>
                      {drillRows.length === 0 ? (
                        <tr><td colSpan={6} className="py-4 text-center text-muted-foreground">No rows</td></tr>
                      ) : drillRows.map((r, i) => (
                        <tr key={i} className="border-b hover:bg-muted/50">
                          <td className="py-1.5 px-2">{r.invoice_date || '-'}</td>
                          <td className="py-1.5 px-2">{r.invoice_number || '-'}</td>
                          <td className="py-1.5 px-2">{r.category_level_2 || r.category_level_1 || '-'}</td>
                          <td className="py-1.5 px-2">{r.buyer_name || '-'}</td>
                          <td className="py-1.5 px-2 text-muted-foreground text-xs">{r.gl_code || ''} {r.cost_center ? `· ${r.cost_center}` : ''}</td>
                          <td className="py-1.5 px-2 text-right font-mono">{fmt(r.spend_amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
